import { AlertTriangle, CircleCheck, Lock, Pencil, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import {
	type EditorState,
	emptyEditor,
	type QueryCandidate,
	type RecommendationDetail,
	type RecommendationPage,
	type RecommendationStatus,
} from "../../types/recommendations";
import { RecommendationEditor } from "./RecommendationEditor";

const STATUS_STYLES: Record<string, string> = {
	draft: "bg-slate-100 text-slate-600",
	approved: "bg-emerald-50 text-emerald-600",
	published: "bg-sky-50 text-sky-600",
};

export const QueriesTab = () => {
	const [candidates, setCandidates] = useState<QueryCandidate[]>([]);
	const [page, setPage] = useState<RecommendationPage | null>(null);
	const [pageNum, setPageNum] = useState(1);
	const [statusFilter, setStatusFilter] = useState("");
	const [search, setSearch] = useState("");
	const [editor, setEditor] = useState<EditorState | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadCandidates = useCallback(async () => {
		setCandidates(
			await fetchJson<QueryCandidate[]>(
				"/recommendations/candidates?days=90&limit=60",
			),
		);
	}, []);

	const loadSaved = useCallback(async () => {
		const params = new URLSearchParams({ page: String(pageNum), limit: "25" });
		if (statusFilter) params.set("status", statusFilter);
		if (search.trim()) params.set("q", search.trim());
		setPage(await fetchJson<RecommendationPage>(`/recommendations?${params}`));
	}, [pageNum, statusFilter, search]);

	useEffect(() => {
		loadCandidates().catch((e) => setError(String(e)));
	}, [loadCandidates]);

	useEffect(() => {
		loadSaved().catch((e) => setError(String(e)));
	}, [loadSaved]);

	const openExisting = useCallback(async (id: number) => {
		const row = await fetchJson<RecommendationDetail>(`/recommendations/${id}`);
		setEditor({
			id: row.id,
			query: row.normalizedQuery,
			products: row.productIds.map((pid) => ({
				productId: pid,
				name: row.products.find((p) => p.productId === pid)?.name ?? `#${pid}`,
			})),
			categories: row.categoryIds.map((cid) => ({
				categoryId: cid,
				name:
					row.categories.find((c) => c.categoryId === cid)?.name ?? `#${cid}`,
			})),
			synonyms: row.synonyms,
			intent: row.intent ?? "",
			notes: row.notes ?? "",
			status: row.status,
			locked: row.locked,
			candidateSnapshot: row.candidateSnapshot,
			model: row.model,
		});
	}, []);

	const save = useCallback(async () => {
		if (!editor) return;
		setSaving(true);
		setError(null);
		try {
			const payload = {
				productIds: editor.products.map((p) => p.productId),
				categoryIds: editor.categories.map((c) => c.categoryId),
				synonyms: editor.synonyms,
				intent: editor.intent,
				notes: editor.notes,
				status: editor.status,
			};

			if (editor.id === null) {
				// Creates are always locked server-side, so `locked` is not sent here.
				await sendJson("/recommendations", "POST", {
					query: editor.query,
					source: "manual",
					...payload,
				});
			} else {
				await sendJson(`/recommendations/${editor.id}`, "PUT", {
					...payload,
					locked: editor.locked,
				});
			}

			setEditor(null);
			await Promise.all([loadSaved(), loadCandidates()]);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Save failed");
		} finally {
			setSaving(false);
		}
	}, [editor, loadSaved, loadCandidates]);

	const remove = useCallback(async () => {
		if (!editor?.id) return;
		await sendJson(`/recommendations/${editor.id}`, "DELETE");
		setEditor(null);
		await Promise.all([loadSaved(), loadCandidates()]);
	}, [editor, loadSaved, loadCandidates]);

	const changeStatus = useCallback(
		async (id: number, status: RecommendationStatus) => {
			await sendJson(`/recommendations/${id}/status`, "PATCH", { status });
			await loadSaved();
		},
		[loadSaved],
	);

	return (
		<div className="space-y-6">
			{error && (
				<p className="rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-600">
					{error}
				</p>
			)}

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
					<div className="mb-4 flex items-center justify-between">
						<h3 className="text-sm font-semibold text-slate-700">
							Top queries (last 90 days)
						</h3>
						<button
							type="button"
							onClick={() => setEditor(emptyEditor())}
							className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-primary hover:text-primary"
						>
							<Plus className="h-3.5 w-3.5" />
							New
						</button>
					</div>
					<div className="max-h-[520px] space-y-1 overflow-y-auto">
						{candidates.map((c) => (
							<button
								type="button"
								key={c.query}
								onClick={() => setEditor(emptyEditor(c.query))}
								className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-[13px] text-slate-700 transition-colors hover:bg-slate-50"
							>
								<span className="flex min-w-0 items-center gap-1.5">
									{c.curated && (
										<CircleCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
									)}
									{c.zeroResultSearches > 0 && (
										<AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
									)}
									<span className="truncate">{c.query}</span>
								</span>
								<span className="shrink-0 text-slate-400">
									{c.searches} searches · {c.products.length} clicked
								</span>
							</button>
						))}
						{candidates.length === 0 && (
							<p className="text-sm text-slate-400">No behavioural data yet</p>
						)}
					</div>
					<p className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-400">
						<CircleCheck className="mr-1 inline h-3 w-3 text-emerald-500" />
						already curated ·
						<AlertTriangle className="mx-1 inline h-3 w-3 text-amber-500" />
						has zero-result searches
					</p>
				</div>

				{editor ? (
					<RecommendationEditor
						state={editor}
						onChange={setEditor}
						onSave={save}
						onDelete={remove}
						onClose={() => setEditor(null)}
						saving={saving}
					/>
				) : (
					<div className="rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-sm text-slate-400 shadow-sm">
						Pick a query on the left to curate it, or press <strong>New</strong>{" "}
						to create an entry for a query with no traffic yet.
					</div>
				)}
			</div>

			<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
				<div className="mb-4 flex flex-wrap items-center gap-3">
					<h3 className="text-sm font-semibold text-slate-700">
						Curated recommendation index
					</h3>
					<input
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPageNum(1);
						}}
						placeholder="Filter by query…"
						className="ml-auto rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] outline-none focus:border-primary"
					/>
					<select
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value);
							setPageNum(1);
						}}
						aria-label="Filter by status"
						className="rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] text-slate-700 outline-none focus:border-primary"
					>
						<option value="">All statuses</option>
						<option value="draft">Draft</option>
						<option value="approved">Approved</option>
						<option value="published">Published</option>
					</select>
				</div>

				<div className="overflow-x-auto">
					<table className="w-full text-left text-[13px]">
						<thead>
							<tr className="border-b border-slate-200 text-slate-500">
								<th className="pb-2 font-medium">Query</th>
								<th className="pb-2 font-medium">Products</th>
								<th className="pb-2 font-medium">Categories</th>
								<th className="pb-2 font-medium">Source</th>
								<th className="pb-2 font-medium">Status</th>
								<th className="pb-2 font-medium">Updated by</th>
								<th className="pb-2 font-medium" />
							</tr>
						</thead>
						<tbody>
							{page?.items.length === 0 && (
								<tr>
									<td colSpan={7} className="py-3 text-slate-400">
										No curated recommendations yet
									</td>
								</tr>
							)}
							{page?.items.map((r) => (
								<tr
									key={r.id}
									className="border-b border-slate-100 last:border-0"
								>
									<td className="py-2 pr-2 font-medium text-slate-700">
										<span className="flex items-center gap-1.5">
											{r.locked && (
												<Lock className="h-3 w-3 shrink-0 text-amber-500" />
											)}
											{r.normalizedQuery}
										</span>
									</td>
									<td className="py-2 pr-2 text-slate-500">
										{r.productIds.length}
									</td>
									<td className="py-2 pr-2 text-slate-500">
										{r.categoryIds.length}
									</td>
									<td className="py-2 pr-2 text-slate-500">{r.source}</td>
									<td className="py-2 pr-2">
										<select
											value={r.status}
											onChange={(e) =>
												changeStatus(
													r.id,
													e.target.value as RecommendationStatus,
												)
											}
											aria-label={`Status for ${r.normalizedQuery}`}
											className={`rounded-full px-2 py-0.5 text-xs outline-none ${
												STATUS_STYLES[r.status] ?? "bg-slate-100 text-slate-600"
											}`}
										>
											<option value="draft">draft</option>
											<option value="approved">approved</option>
											<option value="published">published</option>
										</select>
									</td>
									<td className="py-2 pr-2 text-slate-400">
										{r.updatedBy ?? "—"}
									</td>
									<td className="py-2 text-right">
										<button
											type="button"
											aria-label={`Edit ${r.normalizedQuery}`}
											onClick={() => openExisting(r.id)}
											className="text-slate-400 hover:text-primary"
										>
											<Pencil className="h-4 w-4" />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{page && page.totalPages > 1 && (
					<div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-[13px]">
						<span className="text-slate-400">
							{page.total} entries · page {page.page} of {page.totalPages}
						</span>
						<div className="flex gap-1">
							<button
								type="button"
								disabled={page.page <= 1}
								onClick={() => setPageNum((p) => p - 1)}
								className="rounded-lg border border-slate-200 px-3 py-1 text-slate-600 disabled:opacity-40"
							>
								Previous
							</button>
							<button
								type="button"
								disabled={page.page >= page.totalPages}
								onClick={() => setPageNum((p) => p + 1)}
								className="rounded-lg border border-slate-200 px-3 py-1 text-slate-600 disabled:opacity-40"
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
